import { MouseEvent, useState } from "react";
import dayjs from "dayjs";
import { Mui, MuiIcons } from "@leap4/frontend";

const { ToggleButtonGroup, ToggleButton, Popover, Box, TextField, Button, Stack, Typography } = Mui;
const { CalendarMonth } = MuiIcons;

export const DATE_PRESETS = [
    { label: "7D", days: 7 },
    { label: "14D", days: 14 },
    { label: "30D", days: 30 },
    { label: "90D", days: 90 },
];

export const MAX_SELECTABLE_DAYS = 730;

const YMD = "YYYY-MM-DD";

export const presetRange = (days: number): { start: string; end: string } => ({
    start: dayjs().subtract(days - 1, "day").format(YMD),
    end: dayjs().format(YMD),
});

const matchingPreset = (start: string, end: string): string | null => {
    if (end !== dayjs().format(YMD)) return null;
    return DATE_PRESETS.find((p) => presetRange(p.days).start === start)?.label ?? null;
};

const validate = (from: string, to: string): string | null => {
    const today = dayjs().format(YMD);
    if (!from || !to) return "Pick both a start and an end date.";
    if (from > to) return "The start date must be on or before the end date.";
    if (to > today) return "The end date can't be in the future.";
    if (dayjs(to).diff(dayjs(from), "day") + 1 > MAX_SELECTABLE_DAYS) return `Pick at most ${MAX_SELECTABLE_DAYS} days.`;
    return null;
};

interface ITimeRangeSelectorProps {
    start: string;
    end: string;
    onChange: (start: string, end: string) => void;
}

/** The DSP TimeRangeSelector: presets apply at once; Custom drafts in a popover and applies only on Apply. */
export const TimeRangeSelector = ({ start, end, onChange }: ITimeRangeSelectorProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [from, setFrom] = useState(start);
    const [to, setTo] = useState(end);
    const [touched, setTouched] = useState(false);

    const activePreset = matchingPreset(start, end);
    const isCustom = activePreset === null;
    const error = validate(from, to);
    const minDate = dayjs().subtract(MAX_SELECTABLE_DAYS - 1, "day").format(YMD);
    const maxDate = dayjs().format(YMD);

    const openCustom = (event: MouseEvent<HTMLElement>) => {
        setFrom(start);
        setTo(end);
        setTouched(false);
        setAnchorEl(event.currentTarget);
    };

    const apply = () => {
        setTouched(true);
        if (error) return;
        onChange(from, to);
        setAnchorEl(null);
    };

    const customLabel = isCustom ? `${dayjs(start).format("MMM D")} – ${dayjs(end).format("MMM D, YYYY")}` : "Custom";

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <ToggleButtonGroup
                value={activePreset}
                exclusive
                size="small"
                aria-label="Time range"
                onChange={(_e: MouseEvent<HTMLElement>, label: string | null) => {
                    const preset = DATE_PRESETS.find((p) => p.label === label);
                    if (preset) {
                        const range = presetRange(preset.days);
                        onChange(range.start, range.end);
                    }
                }}
            >
                {DATE_PRESETS.map((preset) => (
                    <ToggleButton key={preset.label} value={preset.label} sx={{ textTransform: "none", px: 2 }}>
                        {preset.label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            <ToggleButton
                value="custom"
                selected={isCustom}
                onChange={openCustom}
                size="small"
                aria-haspopup="dialog"
                aria-expanded={Boolean(anchorEl) || undefined}
                sx={{ textTransform: "none", px: 2 }}
            >
                <CalendarMonth fontSize="small" sx={{ mr: 0.5 }} />
                {customLabel}
            </ToggleButton>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{ paper: { role: "dialog", "aria-label": "Custom date range" } }}
            >
                <Stack spacing={2} sx={{ p: 2, minWidth: 280 }} component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); apply(); }}>
                    <TextField
                        label="From"
                        type="date"
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: minDate, max: maxDate } }}
                        size="small"
                        autoFocus
                    />
                    <TextField
                        label="To"
                        type="date"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: minDate, max: maxDate } }}
                        size="small"
                    />
                    {touched && error && (
                        <Typography variant="caption" color="error" role="alert">
                            {error}
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                        Dates are UTC. Nothing reloads until you press Apply.
                    </Typography>
                    <Button type="submit" variant="contained" size="small">
                        Apply
                    </Button>
                </Stack>
            </Popover>
        </Box>
    );
};
